import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Card, Button, Accordion, Dropdown, Form } from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import CloseIcon from "@enerex/unified-ui-react/icons/CloseIcon";
import Div from "components/Div";
import PageTitle from "components/PageTitle/index";
import Drawer from "components/Drawer";
import FormInput from "components/FormInput/index";

import { PricingMatrixFilters } from "./components/filters/filters";
import FiltersIcon from '@enerex/unified-ui-react/icons/FilterIcon';
import ChartSection from "./components/chartSection";

import { toIsoDate, fromIsoToShort } from "./utils";

// redux
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "./store";

import { set, SubmitHandler, useForm, useWatch } from "react-hook-form";

import {
  setStates,
  fetchStates,
  setSelectedState,
  setSelectedUtility,
  setSuppliers,
  fetchError,
  fetchChartsData,
  setTerms,
  setSelectedTerm,
  setFromDate,
  setToDate,
  setEffectiveFromDate,
  setEffectiveToDate,
  setCommodity,
  setChartsData,
  setSelectedRateClass,
  setSelectedLoadProfile,
  setSelectedLoadZone,
  setSelectedBillingMethod,
  setUsage,
  setDemand,
  setLoadFactor,
  setMarkets,
  setSelectedMarket,
  setSelectedProductType,
  setProductType,
  setCostComponents,
  setSelectedCostComponent,
  setStartFromDate,
  setStartToDate,
  setCustomerClass,
  setTaxIncluded,
  setPor,
} from "./store/actions/index";
import { states } from "./const/statesList";
import { productTypeOptions, termsOptions } from "./const";
import { SelectedFilters } from "./components/filters/SelectedFilters";
import AccordionFilter from "./components/filters/AccordianFilters";
import { HyperThemeDropDown, PricingState } from "./store/state";
import {
  fetchLoadZonesApi,
  fetchRateClassesApi,
  fetchUtilitiesApi,
  fetchUtilitiesByStateApi,
  fetchStatesByUtilityApi,
  fetchBillingMethodApi,
  fetchStatesApi,
  fetchMarketsApi,
  fetchStatesByMarketApi,
  fetchLoadProfilesApi,
  fetchSuppliersListApi,
  fetchCostComponentsApi,
  fetchProductTypeApi,
  fetchCustomerTypeApi,
} from "./api";
import StickyDiv from "components/StickyDiv";
import { useAuth } from "providers/CustomAuthProvider";
import useShowSnackbar from "hooks/useShowSnackbar";
import { saveUserFiltersApi, loadUserFiltersApi, deleteUserFilterApi } from "../../utils/userFilters";
import { STATIC_RANKINGS_DELTA_BEST_RAW } from "./const/nonPremiumStaticRankingsData";

type DropdownLoadingState = {
  states: boolean;
  utilities: boolean;
  rateClasses: boolean;
  loadProfiles: boolean;
  loadZones: boolean;
  billingMethods: boolean;
  adminClients: boolean;
  costComponents: boolean;
  customerTypes: boolean;
};

const getYesterdayDate = () => {
  const date = new Date();
  date.setDate(date.getDate());
  date.setHours(0, 0, 0, 0);
  return date;
};

const toIsoStringFromDate = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseIsoToDate = (iso?: string | null) => {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ALL_OPTION: HyperThemeDropDown = {
  value: "All",
  name: "All",
  isSelected: false,
};

const toNullableBoolean = (selection: any): boolean | null => {
  if (selection?.value === "Yes") return true;
  if (selection?.value === "No") return false;
  return null;
};
const toBooleanFilterOption = (value?: boolean | null) =>
  value === true
    ? { id: "Yes", value: "Yes", name: "Yes" }
    : value === false
    ? { id: "No", value: "No", name: "No" }
    : ALL_OPTION;

/**
 * Market to Default State Mapping
 * Maps market names to their default state names that should be auto-selected
 */
const MARKET_DEFAULT_STATES: Record<string, string> = {
  ERCOT: "Texas",
  // Add more market-to-state mappings here as needed
  // Example: "PJM": "Pennsylvania",
};

/**
 * Helper function to get the default state name for a given market
 * @param marketName - The name of the market (case-insensitive)
 * @returns The default state name for the market, or null if no mapping exists
 */
const getDefaultStateForMarket = (
  marketName: string | null | undefined
): string | null => {
  if (!marketName) return null;

  // Case-insensitive lookup
  const marketKey = Object.keys(MARKET_DEFAULT_STATES).find(
    (key) => key.toLowerCase() === marketName.toLowerCase()
  );

  return marketKey ? MARKET_DEFAULT_STATES[marketKey] : null;
};

const getCustomerClassLabel = (value?: string | null) => {
  switch (value) {
    case "COMM":
      return "Commercial";
    case "RESI":
      return "Residential";
    case "NONRESI":
      return "Non Residential";
    default:
      return "All";
  }
};

const dateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Pricing = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const openFilterDrawer = () => setIsFilterDrawerOpen(true);
  const closeFilterDrawer = () => setIsFilterDrawerOpen(false);

  useEffect(() => {
    document.title = "DataHub - Ranking";
  }, []);

  const [availableStates, setAvailableStates] = useState<HyperThemeDropDown[]>(
    []
  );
  const [availableUtilities, setAvailableUtilities] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableRateClasses, setAvailableRateClasses] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableLoadProfiles, setAvailableLoadProfiles] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableLoadZones, setAvailableLoadZones] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableBillingMethods, setAvailableBillingMethods] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableAdminClients, setAvailableAdminClients] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableProductType, setavailableProductType] = useState<
    HyperThemeDropDown[]
  >([]);
  const [availableCustomerTypes, setAvailableCustomerTypes] = useState<
    HyperThemeDropDown[]
  >([]);
  // const [costComponentWarning, setCostComponentWarning] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState<DropdownLoadingState>({
    states: false,
    utilities: false,
    rateClasses: false,
    loadProfiles: false,
    loadZones: false,
    billingMethods: false,
    adminClients: false,
    costComponents: false,
    customerTypes: false,
  });
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const dispatch = useDispatch();
  const { clientId, isSystemAdmin, accessToken, isPremium } = useAuth();
  const snackbar = useShowSnackbar();
  const pricingState = useSelector(
    (state: { ranking: PricingState }) => state.ranking
  );

  const applyNonPremiumStaticCharts = useCallback(() => {
    dispatch(
      setChartsData(STATIC_RANKINGS_DELTA_BEST_RAW, "priceTrend", false)
    );
  }, [dispatch]);

  const setDropdownLoadingState = useCallback(
    (key: keyof DropdownLoadingState, value: boolean) => {
      setDropdownLoading((prev) => {
        if (prev[key] === value) return prev;
        return { ...prev, [key]: value };
      });
    },
    []
  );

  const statesFetchIdRef = useRef(0);
  const utilitiesFetchIdRef = useRef(0);
  const statesByUtilityFetchIdRef = useRef(0);
  const lastUtilityStateFetchRef = useRef<{
    utility?: string;
    state?: string;
  }>({ utility: undefined, state: undefined });
  const lastStateUtilitiesFetchRef = useRef<{
    mode?: "state" | "market";
    state?: string;
    market?: string;
  }>({ mode: undefined, state: undefined, market: undefined });
  const preventingCycleRef = useRef(false);

  const isResetting = useRef(false);
  const hasAutoAppliedRef = useRef(false);
  const isAutoSelectingRef = useRef(false);
  const costComponentsFetchIdRef = useRef(0);
  const selectedCostComponentsRef = useRef<string[]>([]);
  const hasInitializedFiltersRef = useRef(false);
  // const costComponentsFetchIdRef = useRef(0);
  // const selectedCostComponentsRef = useRef<string[]>([]);
  const prevUtilityValueRef = useRef<string | undefined>(undefined);
  const prevIsTexasRef = useRef<boolean | undefined>(undefined);
  const prevStateValueRef = useRef<string | undefined>(undefined);
  const prevStateObjectRef = useRef<any>(undefined);
  const isInitialLoadRef = useRef(true);
  const isMarketChangingRef = useRef(false);
  const setValueRef = useRef<((name: string, value: any) => void) | null>(null); // Ref to store setValue for use in loadCostComponents
  const [effectiveResetKey, setEffectiveResetKey] = useState(0);

  // Saved filters state
  const [savedFiltersList, setSavedFiltersList] = useState<Array<{ id: string; name: string; filters: any }>>([]);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);
  const [savingFilters, setSavingFilters] = useState(false);
  const savedFiltersLoadedRef = useRef(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState("");

  // Helper function to set "None" option for Rate Class when there are no options
  const setRateClassToNone = useCallback(() => {
    const noneOption = {
      id: "None",
      name: "None",
      value: "None",
    } as any;
    setAvailableRateClasses([noneOption]);
    dispatch(setSelectedRateClass({ value: "None", name: "None" }));
    if (setValueRef.current) {
      setValueRef.current("rateClass", {
        id: "None",
        name: "None",
        value: "None",
      });
    }
  }, [dispatch]);

  // on mount only
  useEffect(() => {
    if (!accessToken) return;

    // DO NOT call fetchStatesApi here - it loads ALL states from the entire system
    // States should only be loaded from the selected market via fetchStatesForMarket
    // This prevents loading all states when we only want market-specific states
    // fetchStatesApi({ userToken: accessToken })
    //   .then(setAvailableStates)
    //   .catch(console.error);

    fetchBillingMethodApi({ userToken: accessToken })
      .then(setAvailableBillingMethods)
      .catch(console.error);

    // Load load profiles on page load since they change rarely
    fetchLoadProfilesApi({ userToken: accessToken })
      .then(setAvailableLoadProfiles)
      .catch(console.error);

    fetchCustomerTypeApi({ userToken: accessToken })
      .then(setAvailableCustomerTypes)
      .catch(console.error);

    // Load all available markets (no commodity filter)
    fetchMarketsApi({ userToken: accessToken })
      .then((markets) => {
        dispatch(setMarkets(markets));

        // Auto-select ERCOT if available (case-insensitive), otherwise first market
        const ercotMarket = markets?.find((m) => {
          const marketName = (m.name || "").toString().toUpperCase().trim();
          return marketName === "ERCOT";
        });
        const defaultMarket = ercotMarket || markets?.[0];

        if (defaultMarket) {
          // Ensure we're passing the complete market object with all properties
          const marketToSelect = {
            value: defaultMarket.value, // EnerexIdentifier
            name: defaultMarket.name,
            commodity: defaultMarket.commodity,
            id: defaultMarket.id,
          };

          // console.log("Auto-selecting market:", {
          //   name: marketToSelect.name,
          //   value: marketToSelect.value,
          //   commodity: marketToSelect.commodity,
          //   isErcot: ercotMarket !== undefined,
          //   fullMarket: marketToSelect
          // });

          dispatch(setSelectedMarket(marketToSelect as any));
        } else {
          console.warn("No markets available to select");
        }
      })
      .catch(console.error);

    if (isSystemAdmin) {
      fetchSuppliersListApi({ userToken: accessToken })
        .then((list) => {
          setAvailableAdminClients([...list]);
        })
        .catch(console.error);
    }
  }, [accessToken, isSystemAdmin, dispatch]);

  const DEFAULT_PRODUCT_TYPE_VALUE = "Fixed";
  const defaultProductTypeOption = {
    name: "Fixed",
    value: DEFAULT_PRODUCT_TYPE_VALUE,
  };
  const getDefaultProductTypeSelection = () => [
    { ...defaultProductTypeOption },
  ];

  const getNextMonthStartDate = () => {
    const today = new Date();
    const day = today.getDate();
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + (day <= 15 ? 1 : 2));
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const convertMonthSelectionToIso = (selection: any) => {
    if (!selection) return null;
    try {
      let baseDate: Date | null = null;
      if (typeof selection.toDate === "function") {
        baseDate = selection.toDate();
      } else if (selection instanceof Date) {
        baseDate = selection;
      } else if (typeof selection === "string") {
        const isoFromShort = toIsoDate(selection);
        if (isoFromShort) {
          const parsed = new Date(`${isoFromShort}T00:00:00Z`);
          if (!Number.isNaN(parsed.getTime())) {
            baseDate = new Date(
              parsed.getUTCFullYear(),
              parsed.getUTCMonth(),
              parsed.getUTCDate()
            );
          }
        } else {
          const parsed = new Date(selection);
          if (!Number.isNaN(parsed.getTime())) {
            baseDate = parsed;
          }
        }
      }

      if (!baseDate) return null;
      const firstDay = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
      return toIsoStringFromDate(firstDay);
    } catch (error) {
      console.error("Error converting month selection", error);
      return null;
    }
  };

  useEffect(() => {
    dispatch(setTerms(termsOptions));
    // Product types are now loaded from API when market is available
    dispatch(setSelectedTerm("12"));
    dispatch(setSelectedProductType([DEFAULT_PRODUCT_TYPE_VALUE]));
  }, [dispatch]);

  useEffect(() => {
    if (!pricingState.effectiveToDate && !pricingState.effectiveFromDate) {
      const defaultEffectiveIso = toIsoStringFromDate(getYesterdayDate());
      dispatch(setEffectiveFromDate(null));
      dispatch(setEffectiveToDate(defaultEffectiveIso));
    }
  }, [pricingState.effectiveToDate, pricingState.effectiveFromDate, dispatch]);

  useEffect(() => {
    if (!pricingState.startToDate && !pricingState.startFromDate) {
      const defaultStartIso = toIsoStringFromDate(getNextMonthStartDate());
      dispatch(setStartFromDate(null));
      dispatch(setStartToDate(defaultStartIso));
    }
  }, [pricingState.startToDate, pricingState.startFromDate, dispatch]);

  useEffect(() => {
    if (!accessToken) return;
    if (!pricingState.selectedMarket?.commodity) return;
    const resolveCommodityLabel = () => {
      // const explicitCommodity = pricingState.commodity;
      // if (explicitCommodity === 1 ) return "Electric";
      // if (explicitCommodity === 2 ) return "Gas";

      const marketCommodity = pricingState.selectedMarket?.commodity;
      if (!marketCommodity) return "Electric";
      return String(marketCommodity).toLowerCase().includes("gas") ||
        String(marketCommodity).toLowerCase().includes("natgas")
        ? "NatGas"
        : "Electric";
    };

    const loadCostComponents = async () => {
      const requestId = ++costComponentsFetchIdRef.current;
      const market = resolveCommodityLabel();
      setDropdownLoadingState("costComponents", true);
      try {
        const list = await fetchCostComponentsApi({
          userToken: accessToken,
          commodity: market,
        });
        if (costComponentsFetchIdRef.current === requestId) {
          const selectedSet = new Set(selectedCostComponentsRef.current);
          let normalized =
            list?.map((item: any) => ({
              ...item,
              isSelected: selectedSet.has(item.value),
            })) || [];

          // On initial load (no previous selection), auto-select "All"
          // so that Pass Through shows "All" and badge is visible.
          if (selectedSet.size === 0 && normalized.length > 0) {
            const allIndex = normalized.findIndex(
              (cc: any) => cc.value === "All"
            );
            if (allIndex !== -1) {
              normalized = normalized.map((cc: any, idx: number) =>
                idx === allIndex ? { ...cc, isSelected: true } : cc
              );
              // Track default selection in the ref so future loads preserve it
              selectedCostComponentsRef.current = ["All"];
              // Sync the form field so the UI shows "All" selected
              const allItem = normalized[allIndex];
              // Use setValueRef if available (setValue might not be in scope)
              if (setValueRef.current) {
                setValueRef.current("passThrough", [
                  {
                    id: allItem.value,
                    name: allItem.name,
                    value: allItem.value,
                  },
                ]);
              }
            }
          }

          dispatch(setCostComponents(normalized));
        }
      } catch (error) {
        console.error("Error loading cost components", error);
        if (costComponentsFetchIdRef.current === requestId) {
          dispatch(setCostComponents([]));
        }
      } finally {
        if (costComponentsFetchIdRef.current === requestId) {
          setDropdownLoadingState("costComponents", false);
        }
      }
    };

    loadCostComponents();
  }, [
    accessToken,
    pricingState.commodity,
    pricingState.selectedMarket?.commodity,
    pricingState.selectedMarket, // Add selectedMarket to ensure effect runs when market changes
    dispatch,
    setDropdownLoadingState,
  ]);

  // Ensure the first term is selected by default
  useEffect(() => {
    if (
      pricingState.terms &&
      pricingState.terms.length > 0 &&
      !pricingState.terms.filter((x: any) => x.isSelected).length
    ) {
      const twelveMonthTerm = pricingState.terms.find(
        (term) => term.value === "12"
      );
      if (twelveMonthTerm) {
        dispatch(setSelectedTerm("12"));
      }
    }
  }, [pricingState.terms, dispatch]);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    getValues,
    trigger,
  } = useForm<any>({
    defaultValues: {
      commodity: "1",
      // stateCode: { id: "All", name: "All" },
      effectiveDate: getYesterdayDate(),
      stateCode: { id: "All", name: "All", value: "All" },
      // effectiveDate: "",
      term: { id: "12", name: "12 Months", value: "12" },
      utility: { id: "All", name: "All", value: "All" },
      rateClass: { id: "All", value: "All", name: "All" },
      loadZone: { id: "All", value: "All", name: "All" },
      billingMethod: { id: "All", value: "All", name: "All" },
      usage: null,
      demand: null,
      loadFactor: null,
      productType: getDefaultProductTypeSelection(),
      startToDate: getNextMonthStartDate(),
      // Prefill Pass Through with default "All"
      passThrough: [{ id: "All", name: "All", value: "All" }],
      taxable: ALL_OPTION,
      por: ALL_OPTION,
      customerType: { id: "COMM", name: "Commercial", value: "COMM" },
    },
  });

  // Store setValue in ref so it can be used in loadCostComponents effect
  setValueRef.current = setValue;

  const watchedState = useWatch({ control, name: "stateCode" });
  const watchedCommodity = useWatch({ control, name: "commodity" });
  const watchedUtility = useWatch({ control, name: "utility" });
  const selectedMarket = pricingState.selectedMarket;

  useEffect(() => {
    if (!watchedState) {
      if (pricingState.selectedState) {
        dispatch(setSelectedState(null));
      }
      return;
    }

    const hasConcreteState =
      watchedState?.value && watchedState.value !== "All";
    const nextState = hasConcreteState
      ? { value: watchedState.value, name: watchedState.name }
      : null;
    const currentValue = pricingState.selectedState?.value || null;
    const nextValue = nextState?.value || null;

    if (currentValue !== nextValue) {
      dispatch(setSelectedState(nextState));
    }
  }, [watchedState, pricingState.selectedState?.value, dispatch]);

  /**
   * fetchStatesForMarket
   *
   * Purpose: Fetches states based on the selected market.
   *
   * When called:
   * - On initial page load when market is selected
   * - When user changes market selection
   * - When utility changes to "All" (user action) to reload states from market
   *
   * API: POST /states/market
   * Body: { marketEnerexIdentifier, commodity }
   *
   * Returns: List of states for the selected market
   */
  const fetchStatesForMarket = useCallback(async (): Promise<{
    list: HyperThemeDropDown[];
    applied: boolean;
  }> => {
    // Ensure we have a valid selected market
    if (!selectedMarket) {
      console.warn("fetchStatesForMarket: selectedMarket is not available");
      setDropdownLoadingState("states", false);
      setAvailableStates([]);
      dispatch(setStates([]));
      return { list: [], applied: true };
    }

    // Get market identifier - should be EnerexIdentifier stored in value property
    const marketEnerexIdentifier = selectedMarket.value;
    const commodityLabel =
      selectedMarket?.commodity &&
      selectedMarket.commodity.toLowerCase().includes("gas")
        ? "NatGas"
        : "Electric";

    // Log for debugging
    // console.log("fetchStatesForMarket called with:", {
    //   marketName: selectedMarket.name,
    //   marketIdentifier: marketEnerexIdentifier,
    //   commodity: commodityLabel,
    //   selectedMarket: selectedMarket
    // });

    if (!accessToken || !marketEnerexIdentifier) {
      console.warn(
        "fetchStatesForMarket: Missing accessToken or marketEnerexIdentifier",
        {
          hasAccessToken: !!accessToken,
          marketEnerexIdentifier,
        }
      );
      setDropdownLoadingState("states", false);
      setAvailableStates([]);
      dispatch(setStates([]));
      return { list: [], applied: true };
    }
    const requestId = ++statesFetchIdRef.current;
    setDropdownLoadingState("states", true);
    try {
      const list = await fetchStatesByMarketApi({
        marketEnerexIdentifier,
        commodity: commodityLabel,
        userToken: accessToken,
      });
      const applied = statesFetchIdRef.current === requestId;
      if (applied) {
        // Validate that we got market-specific states, not all states
        // ERCOT should only have a few states (mainly Texas), not all 50 states
        const statesCount =
          list?.filter((s: any) => s?.name && s.name !== "All")?.length || 0;
        if (statesCount > 20) {
          console.warn(
            "WARNING: Received too many states for market. This might indicate all states were loaded instead of market-specific states.",
            {
              marketName: selectedMarket.name,
              marketIdentifier: marketEnerexIdentifier,
              statesCount: statesCount,
              firstFewStates: list?.slice(0, 5).map((s: any) => s.name),
            }
          );
        }

        setAvailableStates(list);
        dispatch(setStates(list));
      }
      return { list, applied };
    } catch (error) {
      console.error("Error loading states by market", error);
      const applied = statesFetchIdRef.current === requestId;
      if (applied) {
        setAvailableStates([]);
        dispatch(setStates([]));
      }
      return { list: [], applied };
    } finally {
      if (statesFetchIdRef.current === requestId) {
        setDropdownLoadingState("states", false);
      }
    }
  }, [accessToken, selectedMarket, dispatch, setDropdownLoadingState]);

  /**
   * fetchUtilitiesForState
   *
   * Purpose: Fetches utilities based on a specific state selection.
   *
   * When called:
   * - When user selects a specific state (not "All")
   * - During initial load if ERCOT auto-selects Texas
   *
   * API: POST /utility/bystate
   * Body: { enerexStateIdentifier, commodity }
   *
   * Returns: List of utilities for the selected state
   */
  const fetchUtilitiesForState = useCallback(
    async (stateIdentifier?: string): Promise<HyperThemeDropDown[]> => {
      const commodityLabel =
        selectedMarket?.commodity &&
        selectedMarket.commodity.toLowerCase().includes("gas")
          ? "NatGas"
          : "Electric";

      if (!accessToken || !stateIdentifier || stateIdentifier === "All") {
        const defaults = [ALL_OPTION];
        setAvailableUtilities(defaults);
        setDropdownLoadingState("utilities", false);
        return defaults;
      }

      const requestId = ++utilitiesFetchIdRef.current;
      setDropdownLoadingState("utilities", true);
      try {
        const list = await fetchUtilitiesByStateApi({
          stateIdentifier,
          commodity: commodityLabel,
          userToken: accessToken,
        });
        const hasAll = list?.some((item: any) => item?.value === "All");
        const finalList = hasAll ? list : [ALL_OPTION, ...(list || [])];
        if (utilitiesFetchIdRef.current === requestId) {
          setAvailableUtilities(finalList);
        }
        return finalList;
      } catch (error) {
        console.error("Error loading utilities by state", error);
        if (utilitiesFetchIdRef.current === requestId) {
          setAvailableUtilities([ALL_OPTION]);
        }
        return [ALL_OPTION];
      } finally {
        if (utilitiesFetchIdRef.current === requestId) {
          setDropdownLoadingState("utilities", false);
        }
      }
    },
    [accessToken, selectedMarket, setDropdownLoadingState]
  );

  /**
   * fetchStatesForUtilitySelection
   *
   * Purpose: Fetches states based on a specific utility selection.
   *
   * When called:
   * - When user selects a specific utility (not "All")
   *
   * API: POST /states/byutility
   * Body: { enerexUtilityIdentifiers, commodity }
   *
   * Returns: List of states for the selected utility
   *
   * Note: If current state is not in the new list, it resets state to "All"
   */
  const fetchStatesForUtilitySelection = useCallback(
    async (
      utilityIdentifier?: string,
      currentStateValue?: string
    ): Promise<HyperThemeDropDown[]> => {
      const commodityLabel =
        selectedMarket?.commodity &&
        selectedMarket.commodity.toLowerCase().includes("gas")
          ? "NatGas"
          : "Electric";

      if (!accessToken || !utilityIdentifier || utilityIdentifier === "All") {
        return availableStates;
      }

      const requestId = ++statesByUtilityFetchIdRef.current;
      setDropdownLoadingState("states", true);
      try {
        const list = await fetchStatesByUtilityApi({
          utilityIdentifier,
          commodity: commodityLabel,
          userToken: accessToken,
        });
        const hasAll = list?.some((item: any) => item?.value === "All");
        const finalList = hasAll ? list : [ALL_OPTION, ...(list || [])];
        if (statesByUtilityFetchIdRef.current === requestId) {
          setAvailableStates(finalList);
          dispatch(setStates(finalList));
          if (
            currentStateValue &&
            !finalList.some((item) => item.value === currentStateValue)
          ) {
            dispatch(setSelectedState(null));
            setValue("stateCode", { id: "All", name: "All", value: "All" });
          }
        }
        return finalList;
      } catch (error) {
        console.error("Error loading states by utility", error);
        if (statesByUtilityFetchIdRef.current === requestId) {
          const defaults = [ALL_OPTION];
          setAvailableStates(defaults);
          dispatch(setStates(defaults));
          dispatch(setSelectedState(null));
          setValue("stateCode", { id: "All", name: "All", value: "All" });
        }
        return [ALL_OPTION];
      } finally {
        if (statesByUtilityFetchIdRef.current === requestId) {
          setDropdownLoadingState("states", false);
        }
      }
    },
    [
      accessToken,
      selectedMarket,
      dispatch,
      availableStates,
      setDropdownLoadingState,
      setValue,
    ]
  );

  /**
   * Market Change Effect
   *
   * Purpose: Handles market dropdown changes and initializes state/utility dropdowns.
   *
   * When called:
   * - On initial page load when market is selected
   * - When user changes the market selection
   *
   * Behavior:
   * 1. Resets all dependent dropdowns (state, utility, rate class, load zone, etc.) to "All"
   * 2. Loads states from market API (POST /states/market with marketEnerexIdentifier)
   * 3. Auto-selects Texas if market is ERCOT and Texas is available
   * 4. If Texas auto-selected → Load utilities from state API (POST /utility/bystate)
   * 5. If no auto-selection → Load utilities from market API (GET /utilities/market/{marketIdentifier})
   * 6. Marks initial load as complete after loading
   *
   * Note: This is the ONLY place where initial load happens. After this, onChange handlers take over.
   */
  useEffect(() => {
    if (!accessToken) return;
    if (!selectedMarket?.name) {
      setDropdownLoadingState("states", false);
      return;
    }

    // Set market changing flag to prevent state watcher from interfering
    isMarketChangingRef.current = true;

    // debugger;
    const isGasMarket =
      selectedMarket.commodity &&
      (selectedMarket.commodity.toLowerCase().includes("gas") ||
        selectedMarket.commodity.toLowerCase().includes("natgas"));

    // Reset all dependent dropdowns when market changes
    // IMPORTANT: Clear states first to prevent showing all states instead of market-specific states
    setAvailableStates([]);
    dispatch(setStates([]));
    setAvailableUtilities([]);
    // setRateClassToNone(); // Set "None" when market changes
    if (isGasMarket) {
      setAvailableLoadZones([]);
    }
    dispatch(setSelectedState(null));
    dispatch(setSelectedUtility(null));
    // dispatch(setSelectedRateClass(null));
    dispatch(setSelectedLoadProfile(null));
    dispatch(setSelectedLoadZone(null));
    dispatch(setSelectedBillingMethod(null));
    dispatch(setSelectedCostComponent([]));
    selectedCostComponentsRef.current = []; // Reset ref so loadCostComponents will auto-select "None"
    setValue("stateCode", { id: "All", name: "All", value: "All" });
    setValue("utility", { id: "All", name: "All", value: "All" });
    // setValue("rateClass", { id: "All", value: "All", name: "All" });
    setValue("loadProfile", { id: "All", name: "All", value: "All" });
    setValue("loadZone", { id: "All", value: "All", name: "All" });
    setValue("billingMethod", { id: "All", value: "All", name: "All" });
    setValue("passThrough", []);
    // Reset fetch tracking refs
    lastUtilityStateFetchRef.current = { utility: undefined, state: undefined };
    lastStateUtilitiesFetchRef.current = { mode: undefined };

    // Load product types when market changes
    const resolveCommodityNumeric = () => {
      if (!selectedMarket?.commodity) return 1; // default to Electric
      return String(selectedMarket.commodity).toLowerCase().includes("gas") ||
        String(selectedMarket.commodity).toLowerCase().includes("natgas")
        ? 2
        : 1;
    };

    const loadProductTypesOnMarketChange = async () => {
      const commodityValue = resolveCommodityNumeric();
      try {
        const productTypesList = await fetchProductTypeApi({
          userToken: accessToken,
          commodity: commodityValue,
        });

        // Find "Fixed" option - when market changes, always reset to "Fixed" only
        const fixedOption = productTypesList.find(
          (pt: any) =>
            pt.value === DEFAULT_PRODUCT_TYPE_VALUE ||
            pt.name === DEFAULT_PRODUCT_TYPE_VALUE
        );

        // When market changes, always select only "Fixed" (don't preserve previous selections)
        let selectedValues: string[] = [];
        if (fixedOption) {
          const fixedValue = fixedOption.value || fixedOption.name;
          selectedValues = [fixedValue];
        } else if (productTypesList.length > 0) {
          // If "Fixed" doesn't exist, select the first one
          const firstOption = productTypesList[0];
          const firstValue = firstOption.value || firstOption.name;
          selectedValues = [firstValue];
        }

        // Transform to include isSelected property based on selectedValues
        const normalizedProductTypes = productTypesList.map((pt: any) => {
          const ptValue = pt.value || pt.name;
          return {
            ...pt,
            isSelected: selectedValues.includes(ptValue),
          };
        });

        dispatch(setProductType(normalizedProductTypes));

        // Dispatch selected product types
        if (selectedValues.length > 0) {
          dispatch(setSelectedProductType(selectedValues));
          // Also set form value using setValueRef
          if (setValueRef.current) {
            const selectedOptions = normalizedProductTypes
              .filter((pt: any) => pt.isSelected)
              .map((pt: any) => ({
                id: pt.id,
                value: pt.value || pt.name,
                name: pt.name,
              }));
            setValueRef.current("productType", selectedOptions);
          }
        }
      } catch (error) {
        console.error("Error loading product types on market change", error);
        // On error, keep using static options (fallback)
        dispatch(
          setProductType(
            productTypeOptions.map((pt: any) => ({
              ...pt,
              isSelected: pt.value === DEFAULT_PRODUCT_TYPE_VALUE,
            }))
          )
        );
      }
    };

    loadProductTypesOnMarketChange();

    /**
     * Initial Load Function
     *
     * Loads states and utilities based on selected market.
     * Handles ERCOT + Texas auto-selection logic.
     */
    const loadStatesAndDependents = async () => {
      // debugger;
      try {
        // Ensure we have a valid selected market before proceeding
        if (!selectedMarket?.name || !selectedMarket?.value) {
          console.warn("Selected market is not available for initial load", {
            selectedMarket: selectedMarket,
          });
          return;
        }

        // Verify we're using the correct market (should be ERCOT if that's what's selected)
        // console.log("Initial load - Loading states for market:", {
        //   marketName: selectedMarket.name,
        //   marketIdentifier: selectedMarket.value,
        //   commodity: selectedMarket.commodity
        // });

        const { applied, list } = await fetchStatesForMarket();
        if (!applied) {
          console.warn(
            "fetchStatesForMarket did not apply - request was superseded"
          );
          return;
        }

        // console.log("States loaded for market:", {
        //   marketName: selectedMarket.name,
        //   statesCount: list?.length,
        //   firstFewStates: list?.slice(0, 3).map((s: any) => s.name)
        // });

        const validStates = list.filter(
          (state) => state?.name && state.name !== "All"
        );

        // Get default state for the selected market from constant mapping
        const defaultStateName = getDefaultStateForMarket(selectedMarket.name);
        const defaultState = defaultStateName
          ? validStates.find((state) => state.name === defaultStateName)
          : null;

        let stateToSelect = null;

        if (validStates.length === 1 && defaultStateName) {
          // Auto-select if only one state
          stateToSelect = validStates[0];
        } else if (defaultState) {
          // Auto-select default state if market has a default state mapping and it's available
          stateToSelect = defaultState;
        }

        if (stateToSelect) {
          isAutoSelectingRef.current = true;

          dispatch(
            setSelectedState({
              value: stateToSelect.value,
              name: stateToSelect.name,
            })
          );
          setValue("stateCode", {
            id: stateToSelect.value,
            name: stateToSelect.name,
            value: stateToSelect.value,
          });

          // Load utilities based on selected state
          const utilitiesList = await fetchUtilitiesForState(
            stateToSelect.value
          );
          setAvailableUtilities(utilitiesList);

          // Load load zones only for non-gas markets when state is Texas
          if (!isGasMarket) {
            setDropdownLoadingState("loadZones", true);
            fetchLoadZonesApi({
              stateIdentifier: stateToSelect.value,
              userToken: accessToken,
            })
              .then(setAvailableLoadZones)
              .catch(console.error)
              .finally(() => setDropdownLoadingState("loadZones", false));
          } else {
            setAvailableLoadZones([]);
            setDropdownLoadingState("loadZones", false);
          }

          isAutoSelectingRef.current = false;
        } else {
          // No auto-selection, load market-based utilities
          // No auto-selection, load market-based utilities
          // Ensure we're using the correct selected market identifier
          const marketIdentifier = selectedMarket?.value;
          const commodityLabel =
            selectedMarket?.commodity &&
            selectedMarket.commodity.toLowerCase().includes("gas")
              ? "NatGas"
              : "Electric";
          if (!marketIdentifier) {
            console.warn(
              "Market identifier not available for loading utilities",
              {
                selectedMarket: selectedMarket,
              }
            );
            setAvailableUtilities([]);
          } else {
            setDropdownLoadingState("utilities", true);
            fetchUtilitiesApi({
              marketIdentifier,
              commodity: commodityLabel,
              userToken: accessToken,
            })
              .then((utilitiesList) => {
                setAvailableUtilities(utilitiesList);
              })
              .catch((err) => {
                console.error("Error loading utilities for market", {
                  marketIdentifier,
                  marketName: selectedMarket?.name,
                  error: err,
                });
              })
              .finally(() => setDropdownLoadingState("utilities", false));
          }

          dispatch(setSelectedState(null));
          setValue("stateCode", { id: "All", name: "All", value: "All" });
          isAutoSelectingRef.current = false;
        }
        setRateClassToNone(); // Set "None" when market changes

        // Mark initial load as complete after states and utilities are loaded
        isInitialLoadRef.current = false;
        setIsInitialLoadComplete(true);
      } catch (error) {
        console.error("Error loading states by market", error);
        dispatch(setSelectedState(null));
        setValue("stateCode", { id: "All", name: "All", value: "All" });
        isAutoSelectingRef.current = false;
        isInitialLoadRef.current = false;
        setIsInitialLoadComplete(true);
      } finally {
        // Reset market changing flag after loadStatesAndDependents completes
        isMarketChangingRef.current = false;
      }
    };

    loadStatesAndDependents();
  }, [
    selectedMarket,
    accessToken,
    dispatch,
    fetchStatesForMarket,
    fetchUtilitiesForState,
    fetchUtilitiesApi,
    fetchLoadZonesApi,
    setValue,
    setDropdownLoadingState,
  ]);

  // Auto-apply once on initial load when token, selected market, and its states are ready
  // Also auto-apply when we have auto-selected a state from a single-state market
  // IMPORTANT: Wait for initial load to complete before triggering chart API
  useEffect(() => {
    const marketReady = Boolean(pricingState?.selectedMarket?.name);
    const statesLoading = dropdownLoading.states; // Check if states are still loading
    const statesReady =
      Array.isArray(availableStates) &&
      availableStates.filter((s) => s?.name && s.name !== "All").length > 0;
    const stateSelected = Boolean(
      pricingState?.selectedState?.name &&
        pricingState.selectedState.name !== "All"
    );

    // Log current state for debugging
    // console.log("Auto-apply effect - Checking conditions:", {
    //   hasAutoApplied: hasAutoAppliedRef.current,
    //   accessToken: !!accessToken,
    //   marketReady,
    //   statesLoading,
    //   statesReady,
    //   stateSelected,
    //   isInitialLoadComplete,
    //   selectedState: pricingState?.selectedState,
    //   availableStatesCount: availableStates?.length
    // });

    // Don't auto-apply if states are still loading
    if (statesLoading) {
      // console.log("Auto-apply: Waiting for states to finish loading...");
      return;
    }

    // Don't auto-apply if initial load is not complete
    if (!isInitialLoadComplete) {
      // console.log("Auto-apply: Waiting for initial load to complete...");
      return;
    }

    // Don't auto-apply if states are not ready yet
    if (!statesReady) {
      // console.log("Auto-apply: Waiting for states to be populated...");
      return;
    }

    // All conditions met - trigger chart API
    if (
      !hasAutoAppliedRef.current &&
      accessToken &&
      marketReady &&
      // statesReady &&
      // stateSelected &&
      !statesLoading && // Ensure states are not loading
      isInitialLoadComplete && // Ensure initial load is complete
      isPremium !== null // Wait for premium flag resolution
    ) {
      hasAutoAppliedRef.current = true;
      if (isPremium === true) {
        dispatch(fetchChartsData(clientId, accessToken, isPremium));
      } else {
        applyNonPremiumStaticCharts();
      }
    }
  }, [
    pricingState?.selectedMarket,
    pricingState?.selectedState,
    availableStates,
    accessToken,
    dropdownLoading.states, // Re-run when states loading completes
    isInitialLoadComplete, // Re-run when initial load completes
    isPremium,
    applyNonPremiumStaticCharts,
    clientId,
    dispatch,
  ]);

  /**
   * State Watcher Effect
   *
   * Purpose: Handles state dropdown changes and updates utility dropdown accordingly.
   *
   * When called:
   * - On initial load (market-based loading)
   * - When user selects a specific state
   * - When user changes state to "All"
   *
   * Behavior:
   * 1. If both state and utility are "All" → Load utilities from market API (GET /utilities/market/{marketIdentifier})
   * 2. If specific state selected → Load utilities from state API (POST /utility/bystate)
   * 3. If state is "All" → Load utilities from market API (GET /utilities/market/{marketIdentifier})
   * 4. Also loads load zones when specific state is selected (for non-gas markets)
   *
   * Note: Only clears/reloads utilities when state value actually changes to prevent unnecessary API calls
   */
  useEffect(() => {
    // debugger;
    // Skip during auto-selection, initial load, or market change to prevent interference
    if (
      isAutoSelectingRef.current ||
      isInitialLoadRef.current ||
      isMarketChangingRef.current
    ) {
      return;
    }

    if (!watchedState || watchedCommodity == null || !accessToken) {
      setAvailableUtilities([]);
      setDropdownLoadingState("utilities", false);
      setDropdownLoadingState("loadZones", false);
      return;
    }

    // Extract state value (handle both string and object formats)
    const currentStateValueRaw = watchedState?.value;
    const currentStateValue =
      typeof currentStateValueRaw === "object" && currentStateValueRaw !== null
        ? currentStateValueRaw?.value || currentStateValueRaw?.id || null
        : currentStateValueRaw;

    // Normalize "All" values for "All" check
    const normalizedCurrentState =
      !currentStateValue ||
      currentStateValue === "All" ||
      currentStateValue === null
        ? "All"
        : currentStateValue;
    const currentStateIsAll = normalizedCurrentState === "All";

    // Check if state object reference changed (detects user interaction even if value is same)
    const stateObjectChanged = prevStateObjectRef.current !== watchedState;

    // Check if state actually changed by comparing current normalized value with previous
    // Use a more reliable comparison that handles undefined -> "All" transitions
    const prevStateRaw = prevStateValueRef.current;
    const normalizedPrevState =
      !prevStateRaw || prevStateRaw === null ? "" : prevStateRaw;
    const prevStateWasAll = normalizedPrevState === "All";

    // State changed if normalized values are different OR if previous was undefined and current is "All"
    // OR if state object reference changed AND current state is "All" (user explicitly selected "All" from dropdown)
    const stateChanged =
      normalizedPrevState !== normalizedCurrentState ||
      (prevStateRaw === undefined && normalizedCurrentState === "All") ||
      (stateObjectChanged && currentStateIsAll && !isInitialLoadRef.current);

    const stateChangedToAll = !prevStateWasAll && currentStateIsAll;

    // Update refs for next comparison
    prevStateValueRef.current = normalizedCurrentState;
    prevStateObjectRef.current = watchedState;

    if (isResetting.current) {
      isResetting.current = false;
    } else if (stateChanged) {
      // Only clear utilities when state actually changes
      setAvailableUtilities([]);
      setRateClassToNone(); // Set "None" when state changes
      setDropdownLoadingState("rateClasses", false);
    }

    const isGasMarket =
      selectedMarket?.commodity &&
      (selectedMarket.commodity.toLowerCase().includes("gas") ||
        selectedMarket.commodity.toLowerCase().includes("natgas"));

    const marketIdentifier = selectedMarket?.value;
    const isStateAll = currentStateIsAll;
    const isTexas = watchedState?.name === "Texas";
    // Check if utility is "All" - handle both string and object formats
    const utilityValueRaw = watchedUtility?.value;
    const utilityValue =
      typeof utilityValueRaw === "object" && utilityValueRaw !== null
        ? utilityValueRaw?.value || utilityValueRaw?.id || null
        : utilityValueRaw;
    const isUtilityAll =
      !utilityValue || utilityValue === "All" || utilityValue === null;
    const bothAll = isStateAll && isUtilityAll;

    // Case 1: State is "All" → Load utilities from market
    // Called when: User changes state to "All" (after initial load)
    // API: GET /utilities/market/{marketIdentifier}
    // Always reload when state is "All" (user action) or if it's initial load
    if (isStateAll && marketIdentifier) {
      // When state is "All", set rate class to "None" immediately
      if (!isTexas) {
        setRateClassToNone();
      }
      // Always reload utilities from market when state is "All"
      // If it's initial load, always reload
      // If not initial load, reload if:
      //   - State changed (including undefined -> "All" transition)
      //   - OR last fetch was from state API (meaning user had a specific state before)
      //   - OR last fetch mode is undefined (first time after initial load)
      // This ensures utilities reload even if state was already "All" before initial load
      const last = lastStateUtilitiesFetchRef.current;
      const shouldReload =
        isInitialLoadRef.current ||
        (!isInitialLoadRef.current &&
          (stateChanged ||
            stateChangedToAll ||
            last.mode === undefined ||
            last.mode === "state" ||
            (last.mode === "market" && last.market !== marketIdentifier)));

      if (shouldReload) {
        lastStateUtilitiesFetchRef.current = {
          mode: "market",
          market: marketIdentifier,
          state: "All",
        };
        const commodityLabel =
          selectedMarket?.commodity &&
          selectedMarket.commodity.toLowerCase().includes("gas")
            ? "NatGas"
            : "Electric";
        setDropdownLoadingState("utilities", true);
        fetchUtilitiesApi({
          marketIdentifier,
          commodity: commodityLabel,
          userToken: accessToken,
        })
          .then((list) => {
            setAvailableUtilities(list);
            // When state is "All", set rate class to "None" since we can't fetch rate classes without a specific state
            if (!isTexas) {
              setRateClassToNone();
            }
          })
          .catch((err) => console.error("Error loading utilities", err))
          .finally(() => setDropdownLoadingState("utilities", false));
      } else {
        // Even if utilities aren't reloaded, set rate class to "None" when state is "All"
        if (!isTexas) {
          setRateClassToNone();
        }
      }
    }
    // Case 2: Specific state selected → Load utilities based on state
    // Called when: User selects a specific state (not "All")
    // API: POST /utility/bystate with state identifier
    // Only reload if state actually changed to preserve utilities when state hasn't changed
    else if (!isStateAll && currentStateValue && currentStateValue !== "All") {
      if (stateChanged) {
        preventingCycleRef.current = false;
        const last = lastStateUtilitiesFetchRef.current;
        if (last.mode !== "state" || last.state !== currentStateValue) {
          lastStateUtilitiesFetchRef.current = {
            mode: "state",
            state: currentStateValue,
            market: marketIdentifier,
          };
          fetchUtilitiesForState(currentStateValue).then(() => {
            // After utilities are fetched, check if utility is "All" or not selected
            // If so, set rate class to "None" since we can't fetch rate classes without a utility
            const currentIsTexas = watchedState?.name === "Texas";
            const utilityValueRaw = watchedUtility?.value;
            const utilityValue =
              typeof utilityValueRaw === "object" && utilityValueRaw !== null
                ? utilityValueRaw?.value || utilityValueRaw?.id || null
                : utilityValueRaw;
            const normalizedUtilityValue =
              !utilityValue || utilityValue === "All" || utilityValue === null
                ? "All"
                : utilityValue;

            if (normalizedUtilityValue === "All" && !currentIsTexas) {
              // No utility selected, set rate class to "None"
              setRateClassToNone();
            }
          });
        }
      }
      // If state didn't change, preserve existing utilities list
    } else {
      lastStateUtilitiesFetchRef.current = { mode: undefined };
      setAvailableUtilities([]);
      setDropdownLoadingState("utilities", false);
      // When state is "All" or invalid, set rate class to "None"
      const currentIsTexas = watchedState?.name === "Texas";
      if (!currentIsTexas) {
        setRateClassToNone();
      }
    }

    if (!isGasMarket && !isStateAll && currentStateValue) {
      // Only fetch load zones when not gas market, state is Texas, and a specific state is selected (not "All")
      setDropdownLoadingState("loadZones", true);
      fetchLoadZonesApi({
        stateIdentifier: currentStateValue,
        userToken: accessToken,
      })
        .then(setAvailableLoadZones)
        .catch(console.error)
        .finally(() => setDropdownLoadingState("loadZones", false));
    } else {
      setAvailableLoadZones([]);
      setDropdownLoadingState("loadZones", false);
    }
  }, [
    watchedState,
    watchedUtility,
    watchedCommodity,
    selectedMarket,
    accessToken,
    setDropdownLoadingState,
    fetchUtilitiesForState,
    fetchUtilitiesApi,
    fetchLoadZonesApi,
    setRateClassToNone,
  ]);

  /**
   * Helper function to call rate class API after state API completes
   * This ensures rate class API is only called after state processing is done
   */
  const callRateClassApiAfterStateComplete = useCallback(() => {
    if (!accessToken) {
      setAvailableRateClasses([]);
      setDropdownLoadingState("rateClasses", false);
      return;
    }

    const utilityValueRaw = watchedUtility?.value;
    const utilityValue =
      typeof utilityValueRaw === "object" && utilityValueRaw !== null
        ? utilityValueRaw?.value || utilityValueRaw?.id || null
        : utilityValueRaw;

    const normalizedUtilityValue =
      !utilityValue || utilityValue === "All" || utilityValue === null
        ? "All"
        : utilityValue;
    const hasUtilitySelection = normalizedUtilityValue !== "All";

    // Use getValues to get current form value instead of watchedState (which may be stale)
    const currentStateFromForm = getValues("stateCode");
    const isTexas = currentStateFromForm?.name === "Texas";

    if (!hasUtilitySelection) {
      // When utility is "All", set rate class to "None"
      if (!isTexas) {
        setRateClassToNone();
      }
      setAvailableRateClasses([]);
      setDropdownLoadingState("rateClasses", false);
      return;
    }
    if (isTexas) {
      setAvailableRateClasses([]);
      setDropdownLoadingState("rateClasses", false);
      return;
    }

    const commodity =
      selectedMarket?.commodity === "Electricity" ||
      selectedMarket?.commodity === "Electric"
        ? 1
        : 2;

    setDropdownLoadingState("rateClasses", true);
    const currentRateClass = getValues("rateClass");
    fetchRateClassesApi({
      utilityIdentifier: normalizedUtilityValue,
      commodity,
      userToken: accessToken,
    })
      .then((list) => {
        // If there are no real rate classes (only the synthetic "All" option),
        // show "None" in the UI instead of "All".
        // Check if list is empty OR only contains "All" option(s)
        const realRateClasses = Array.isArray(list)
          ? list.filter((item: any) => item?.value && item.value !== "All")
          : [];

        const hasOnlyAllOption =
          !Array.isArray(list) ||
          list.length === 0 ||
          realRateClasses.length === 0;

        if (hasOnlyAllOption) {
          setRateClassToNone();
        } else {
          // Normal case: list includes real rate classes (and an "All" option)
          setAvailableRateClasses(list);
          const selectedValue = currentRateClass?.value;
          if (
            selectedValue &&
            selectedValue !== "All" &&
            selectedValue !== "None" &&
            Array.isArray(list)
          ) {
            const match = list.find(
              (item: any) => item.value === selectedValue
            );
            if (match) {
              setValue("rateClass", match);
            }
          } else if (
            !selectedValue ||
            selectedValue === "All" ||
            selectedValue === "None"
          ) {
            // Ensure form has a default value when real options are available
            if (setValueRef.current) {
              setValueRef.current("rateClass", {
                id: "All",
                name: "All",
                value: "All",
              });
            }
            dispatch(setSelectedRateClass({ value: "All", name: "All" }));
          }
        }
      })
      .catch((err) => {
        console.error("Error loading rate-classes", err);
        setRateClassToNone();
      })
      .finally(() => setDropdownLoadingState("rateClasses", false));
  }, [
    accessToken,
    watchedUtility,
    selectedMarket,
    getValues,
    setRateClassToNone,
    setAvailableRateClasses,
    setDropdownLoadingState,
    setValue,
    dispatch,
  ]);

  /**
   * Utility Watcher Effect
   *
   * Purpose: Handles utility dropdown changes and updates state dropdown accordingly.
   *
   * When called:
   * - On initial load (but skipped if isInitialLoadRef is true)
   * - When user selects a specific utility
   * - When user changes utility to "All"
   *
   * Behavior:
   * 1. If specific utility selected → Load states from utility API (POST /states/byutility)
   * 2. If utility changes to "All" and state is specific → Reload utilities from market API
   * 3. If utility changes to "All" (user action) → Reload states from market API (GET /states/market)
   * 4. Prevents cyclic API calls when both are "All"
   */
  useEffect(() => {
    // Skip during market change to prevent rate class API from being called when utility is reset to "All"
    if (isMarketChangingRef.current) {
      return;
    }

    // Extract utility value (handle both string and object formats)
    // debugger;
    const currentUtilityValueRaw = watchedUtility?.value;
    const currentUtilityValue =
      typeof currentUtilityValueRaw === "object" &&
      currentUtilityValueRaw !== null
        ? currentUtilityValueRaw?.value || currentUtilityValueRaw?.id || null
        : currentUtilityValueRaw;

    // Extract state value (handle both string and object formats)
    const currentStateValueRaw = watchedState?.value;
    const currentStateValue =
      typeof currentStateValueRaw === "object" && currentStateValueRaw !== null
        ? currentStateValueRaw?.value || currentStateValueRaw?.id || null
        : currentStateValueRaw;

    // Normalize values for comparison
    const normalizedUtilityValue =
      !currentUtilityValue ||
      currentUtilityValue === "All" ||
      currentUtilityValue === null
        ? "All"
        : currentUtilityValue;
    const normalizedStateValue =
      !currentStateValue ||
      currentStateValue === "All" ||
      currentStateValue === null
        ? "All"
        : currentStateValue;

    const hasUtilitySelection = normalizedUtilityValue !== "All";
    const isStateAll = normalizedStateValue === "All";
    const isUtilityAll = normalizedUtilityValue === "All";
    const bothAll = isStateAll && isUtilityAll;
    // const isTexas = watchedState?.name === "Texas";
    const currentStateFromForm = getValues("stateCode");
    const isTexas = currentStateFromForm?.name === "Texas";
    // Get default state for the selected market from constant mapping
    const defaultStateName = getDefaultStateForMarket(selectedMarket?.name);
    const isMarketWithDefaultState = defaultStateName !== null;

    const utilityChanged =
      prevUtilityValueRef.current !== normalizedUtilityValue;
    const texasChanged = prevIsTexasRef.current !== isTexas;
    prevUtilityValueRef.current = normalizedUtilityValue;
    prevIsTexasRef.current = isTexas;

    // Prevent cycles: if we're currently preventing cycles and both are "All", skip
    if (bothAll && preventingCycleRef.current) {
      lastUtilityStateFetchRef.current = {
        utility: normalizedUtilityValue,
        state: normalizedStateValue,
      };
      return;
    }

    // Case 1: Specific utility selected → Load states based on utility
    // Called when: User selects a specific utility (not "All")
    // API: POST /states/byutility with utility identifier
    // After loading states, check if Texas is selected, if yes load utilities from state API
    if (hasUtilitySelection && accessToken) {
      preventingCycleRef.current = false;
      const last = lastUtilityStateFetchRef.current;
      if (
        last?.utility !== normalizedUtilityValue ||
        last?.state !== normalizedStateValue
      ) {
        lastUtilityStateFetchRef.current = {
          utility: normalizedUtilityValue,
          state: normalizedStateValue,
        };

        // Load states from utility API first
        fetchStatesForUtilitySelection(currentUtilityValue, currentStateValue)
          .then((statesList) => {
            // After states are loaded, check if market has default state logic applies
            let stateApiPromise: Promise<any> = Promise.resolve();

            if (isMarketWithDefaultState && defaultStateName && statesList) {
              const defaultState = statesList.find(
                (s: any) => s?.name === defaultStateName
              );
              // Use getValues to get current form value instead of watchedState (which may be stale)
              const currentStateFromForm = getValues("stateCode");
              const isDefaultStateSelected =
                currentStateFromForm?.name === defaultStateName;

              if (defaultState && !isDefaultStateSelected) {
                // Auto-select default state if not already selected
                isAutoSelectingRef.current = true;
                dispatch(
                  setSelectedState({
                    value: defaultState.value,
                    name: defaultState.name,
                  })
                );
                setValue("stateCode", {
                  id: defaultState.value,
                  name: defaultState.name,
                  value: defaultState.value,
                });

                // Load utilities from state API (default state)
                stateApiPromise = fetchUtilitiesForState(defaultState.value);
                isAutoSelectingRef.current = false;
              } else if (isDefaultStateSelected && currentStateValue) {
                // Default state is already selected, load utilities from state API
                stateApiPromise = fetchUtilitiesForState(currentStateValue);
              }
            } else if (isTexas && currentStateValue) {
              // Texas is selected (not a market with default state), load utilities from state API
              stateApiPromise = fetchUtilitiesForState(currentStateValue);
            }

            // After state API completes, call rate class API
            stateApiPromise
              .then(() => {
                callRateClassApiAfterStateComplete();
              })
              .catch((error) => {
                console.error(
                  "Error in state API, still calling rate class API",
                  error
                );
                callRateClassApiAfterStateComplete();
              });
          })
          .catch((error) => {
            console.error("Error loading states for utility", error);
            // Even on error, try to call rate class API
            callRateClassApiAfterStateComplete();
          });
      } else {
        // Utility/state combination hasn't changed, but utility changed, so call rate class API
        if (utilityChanged) {
          callRateClassApiAfterStateComplete();
        }
      }
    }
    // Case 2: Utility changed to "All" (user action) → Reload states from market
    // Called when: User changes utility to "All" (regardless of state value)
    // API: GET /states/market/{marketIdentifier} - Reload states from market
    // After loading states, check ERCOT+Texas, if yes auto-select Texas and load utilities from state API
    // If not Texas, load utilities from market API
    else if (
      isUtilityAll &&
      utilityChanged &&
      accessToken &&
      selectedMarket?.name &&
      !isInitialLoadRef.current
    ) {
      const marketIdentifier = selectedMarket?.value;
      if (marketIdentifier && !preventingCycleRef.current) {
        preventingCycleRef.current = true;
        lastUtilityStateFetchRef.current = {
          utility: normalizedUtilityValue,
          state: normalizedStateValue,
        };
        lastStateUtilitiesFetchRef.current = {
          mode: "market",
          market: marketIdentifier,
          state: normalizedStateValue,
        };

        // Get default state for the selected market from constant mapping
        const utilityDefaultStateName = getDefaultStateForMarket(
          selectedMarket?.name
        );
        const utilityIsMarketWithDefaultState =
          utilityDefaultStateName !== null;

        // Reload states from market
        fetchStatesForMarket()
          .then(({ list }) => {
            setAvailableStates(list);
            dispatch(setStates(list));

            // Check if market has default state logic applies
            if (
              utilityIsMarketWithDefaultState &&
              utilityDefaultStateName &&
              list
            ) {
              const defaultState = list.find(
                (s: any) => s?.name === utilityDefaultStateName
              );
              if (defaultState) {
                // Auto-select default state
                isAutoSelectingRef.current = true;
                dispatch(
                  setSelectedState({
                    value: defaultState.value,
                    name: defaultState.name,
                  })
                );
                setValue("stateCode", {
                  id: defaultState.value,
                  name: defaultState.name,
                  value: defaultState.value,
                });

                // Load utilities from state API (default state)
                fetchUtilitiesForState(defaultState.value);
                isAutoSelectingRef.current = false;
              } else {
                // No default state found, reset state to "All" and load utilities from market
                dispatch(setSelectedState(null));
                setValue("stateCode", { id: "All", name: "All", value: "All" });

                const commodityLabel =
                  selectedMarket?.commodity &&
                  selectedMarket.commodity.toLowerCase().includes("gas")
                    ? "NatGas"
                    : "Electric";
                setDropdownLoadingState("utilities", true);
                fetchUtilitiesApi({
                  marketIdentifier,
                  commodity: commodityLabel,
                  userToken: accessToken,
                })
                  .then((utilitiesList) => {
                    setAvailableUtilities(utilitiesList);
                  })
                  .catch((err) => {
                    console.error("Error loading utilities for market", err);
                  })
                  .finally(() => setDropdownLoadingState("utilities", false));
              }
            } else {
              // Not a market with default state, reset state to "All" and load utilities from market
              if (isStateAll) {
                dispatch(setSelectedState(null));
                setValue("stateCode", { id: "All", name: "All", value: "All" });
              }

              const commodityLabel =
                selectedMarket?.commodity &&
                selectedMarket.commodity.toLowerCase().includes("gas")
                  ? "NatGas"
                  : "Electric";
              setDropdownLoadingState("utilities", true);
              fetchUtilitiesApi({
                marketIdentifier,
                commodity: commodityLabel,
                userToken: accessToken,
              })
                .then((utilitiesList) => {
                  setAvailableUtilities(utilitiesList);
                })
                .catch((err) => {
                  console.error("Error loading utilities for market", err);
                })
                .finally(() => setDropdownLoadingState("utilities", false));
            }

            // Reset cycle prevention after a short delay
            setTimeout(() => {
              preventingCycleRef.current = false;
            }, 100);
          })
          .catch((error) => {
            console.error("Error loading states for market", error);
            preventingCycleRef.current = false;
          });
      }
    } else {
      lastUtilityStateFetchRef.current = {
        utility: currentUtilityValue,
        state: currentStateValue,
      };
    }

    if (!hasUtilitySelection || !accessToken) {
      if (utilityChanged || texasChanged) {
        setAvailableRateClasses([]);
        setDropdownLoadingState("rateClasses", false);
      }
      return;
    }

    if (isTexas) {
      if (texasChanged) {
        setValue("loadProfile", { id: "All", name: "All", value: "All" });
        dispatch(setSelectedLoadProfile(null));
        setAvailableRateClasses([]);
      }
      setDropdownLoadingState("rateClasses", false);
      return;
    } else if (texasChanged) {
      // Switched away from Texas; ensure load profile reset once
      dispatch(setSelectedLoadProfile(null));
      setValue("loadProfile", { id: "All", name: "All", value: "All" });
    }

    // Rate class API is now called only after state API completes (in callRateClassApiAfterStateComplete)
    // This ensures state API is called first, then rate class API
  }, [
    watchedUtility,
    watchedState,
    selectedMarket,
    accessToken,
    dispatch,
    setDropdownLoadingState,
    fetchStatesForUtilitySelection,
    fetchStatesForMarket,
    fetchUtilitiesApi,
    getValues,
    setValue,
    setRateClassToNone,
  ]);

  // // When Utility changes, fetch rate classes
  // useEffect(() => {
  //   if (!isFilterDrawerOpen || !watchedRateClass) return;
  //   dispatch(fetchSuppliersApi(watchedRateClass.value));
  //   // or dispatch({ type: FETCH_SUPPLIERS, payload: watchedRateClass.value });
  // }, [watchedRateClass, isFilterDrawerOpen, dispatch]);

  useEffect(() => {
    const currentState =
      pricingState.states.filter((x) => x.isSelected)[0]?.value || "All";
    const currentTerm = pricingState.terms.filter((x) => x.isSelected)[0];
    const currentTermValue = currentTerm?.value || "12";
    const currentUtility = ""; // = pricingState.utilities.filter((x) => x.isSelected)[0];
    // const currentUtilityValue = currentUtility?.value || null;
    const currentFromDate = pricingState.fromDate ? pricingState.fromDate : "";
    const currentToDate = pricingState.toDate ? pricingState.toDate : "";

    const filtersInState = {
      commodity: String(pricingState.commodity || 1),
      stateCode: pricingState.selectedState
        ? {
            id: pricingState.selectedState.value,
            name: pricingState.selectedState.name,
            value: pricingState.selectedState.value,
          }
        : { id: "All", name: "All", value: "All" },
      term: currentTerm
        ? {
            id: currentTermValue,
            name: currentTerm.name,
            value: currentTermValue,
          }
        : { id: "12", name: "12 Months", value: "12" },
      effectiveDate:
        parseIsoToDate(pricingState.effectiveToDate) ||
        parseIsoToDate(pricingState.effectiveFromDate) ||
        getYesterdayDate(),
      // utility: {id: currentUtility?.value, name: currentUtility?.name, value: currentUtility?.value}
      utility: pricingState.selectedUtility
        ? {
            id: pricingState.selectedUtility.value,
            name: pricingState.selectedUtility.name,
            value: pricingState.selectedUtility.value,
          }
        : { id: "All", name: "All", value: "All" },
      rateClass: pricingState.selectedRateClass
        ? {
            id: pricingState.selectedRateClass.value,
            value: pricingState.selectedRateClass.value,
            name: pricingState.selectedRateClass.name,
          }
        : { id: "All", value: "All", name: "All" },
      loadZone: pricingState.selectedLoadZone
        ? {
            id: pricingState.selectedLoadZone.value,
            value: pricingState.selectedLoadZone.value,
            name: pricingState.selectedLoadZone.name,
          }
        : { id: "All", value: "All", name: "All" },
      billingMethod: pricingState.selectedBillingMethod
        ? {
            id: pricingState.selectedBillingMethod.value,
            value: pricingState.selectedBillingMethod.value,
            name: pricingState.selectedBillingMethod.name,
          }
        : { id: "All", value: "All", name: "All" },
      productType: (() => {
        const selectedProductType =
          pricingState.productTypes
            ?.filter((pt: any) => pt.isSelected)
            ?.map((pt: any) => ({ name: pt.name, value: pt.value })) || [];

        if (selectedProductType.length > 0) {
          return selectedProductType;
        }

        // Prefill default "Fixed" option to match defaultValues shape
        return getDefaultProductTypeSelection();
      })(),
      // Pass Through:
      // If there are selected cost components in Redux, use them.
      // Otherwise, default to "All" so it is prefilled and badge is visible.
      passThrough: (() => {
        const selectedPassThrough =
          pricingState.costComponents
            ?.filter((cc: any) => cc.isSelected)
            ?.map((cc: any) => ({ name: cc.name, value: cc.value })) || [];

        if (selectedPassThrough.length > 0) {
          return selectedPassThrough;
        }

        // Prefill default "All" option to match defaultValues shape
        return [{ id: "All", name: "All", value: "All" }];
      })(),
      customerType: pricingState.customerClass
        ? {
            id: pricingState.customerClass,
            value: pricingState.customerClass,
            name: getCustomerClassLabel(pricingState.customerClass),
          }
        : { id: "All", value: "All", name: "All" },
      usage: pricingState.usage ?? null,
      demand: pricingState.demand ?? null,
      loadFactor: pricingState.loadFactor ?? null,
      taxable: toBooleanFilterOption(pricingState.taxIncluded),
      por: toBooleanFilterOption(pricingState.por),
      startToDate: (() => {
        const startDateSelection =
          parseIsoToDate(pricingState.startToDate) || getNextMonthStartDate();
        return startDateSelection;
      })(),
    };

    if (hasInitializedFiltersRef.current) {
      return;
    }

    isResetting.current = true;
    reset(filtersInState);
    hasInitializedFiltersRef.current = true;
  }, [
    pricingState.selectedState,
    pricingState.selectedUtility,
    pricingState.selectedRateClass,
    pricingState.selectedLoadZone,
    pricingState.selectedBillingMethod,
    pricingState.terms,
    pricingState.effectiveFromDate,
    pricingState.effectiveToDate,
      pricingState.productTypes,
      pricingState.costComponents,
      pricingState.customerClass,
      reset,
    ]);

  // }, [isFilterDrawerOpen]);

  // // Reset dependant dropdowns when state changes
  // useEffect(() => {
  //   setAvailableRateClasses([{name: 'All', value: 'All', isSelected: true}]);
  //   dispatch(setSelectedRateClass(null));
  // }, [availableUtilities]);

  const onSubmit: SubmitHandler<any> = async (data) => {
    if (!accessToken) {
      console.warn("Access token not ready; skipping chart fetch");
      return;
    }
    if (isPremium !== true) {
      applyNonPremiumStaticCharts();
      return;
    }
    // setFilters(data);
    // closeFilterDrawer();
    const {
      commodity,
      stateCode,
      term,
      effectiveDate,
      utility,
      rateClass,
      loadProfile,
      usage,
      demand,
      loadFactor,
      productType,
      passThrough,
      customerType,
      taxable,
      por,
    } = data;
    const chartKeys = [
      "priceTrend",
      "priceTrendMinMax",
      "forwardCurve",
      "forwardCurveMinMax",
    ];
    // Set isLoading true for all chart keys
    chartKeys.forEach((key) => {
      // dispatch(setChartsDataLoader(true, key));
      dispatch(setChartsData(null, key)); // Optional: clear old data
    });

    const selectedState =
      stateCode?.value && stateCode?.value !== "All" ? stateCode.value : "";
    const selectedUtilities =
      utility?.value && utility?.value !== "All" ? utility.value : "";
    const selectedTerm = term?.value && term?.value !== "All" ? term.value : "";
    const selectedProductType = productType?.map((pt) => pt.value) || [];
    const selectedPassThroughRaw =
      passThrough?.map((item: any) => item?.value ?? item) || [];
    const hasAllPassThroughSelected =
      selectedPassThroughRaw.includes("All");
    const displayPassThrough = selectedPassThroughRaw.filter(
      (value) => value && value !== "All"
    );
    const uiSelectedPassThrough =
      displayPassThrough.length > 0
        ? displayPassThrough
        : hasAllPassThroughSelected
        ? ["All"]
        : [];
    // productType?.value && productType?.value !== " " ? productType.value : '';

    // Derive commodity from selected market instead of form data
    const marketCommodity =
      selectedMarket?.commodity === "Electricity" ||
      selectedMarket?.commodity === "Electric"
        ? 1
        : 2;
    dispatch(setCommodity(marketCommodity));
    dispatch(
      setSelectedState(
        data.stateCode
          ? {
              value: data.stateCode.value,
              name: data.stateCode.name,
            }
          : null
      )
    );
    // dispatch(setSelectedUtilities(selectedUtilities || ''));
    dispatch(setSelectedUtility(data.utility ?? null));
    const normalizedRateClass =
      rateClass && rateClass.value && rateClass.value !== "All"
        ? {
            value: rateClass.value,
            name: rateClass.name || rateClass.label || rateClass.value,
          }
        : null;
    dispatch(setSelectedRateClass(normalizedRateClass));
    if (normalizedRateClass) {
      setValue("rateClass", normalizedRateClass);
    }

    dispatch(setSelectedLoadZone(data.loadZone ?? null));
    dispatch(setSelectedBillingMethod(data.billingMethod ?? null));
    dispatch(setSelectedTerm(selectedTerm));
    const normalizedLoadProfile =
      loadProfile && loadProfile.value && loadProfile.value !== "All"
        ? {
            value: loadProfile.value,
            name: loadProfile.name || loadProfile.label || loadProfile.value,
          }
        : null;
    dispatch(setSelectedLoadProfile(normalizedLoadProfile));
    if (normalizedLoadProfile) {
      setValue("loadProfile", normalizedLoadProfile);
    }

    //product type
    dispatch(setSelectedProductType(selectedProductType || []));

    //cost component
    dispatch(setSelectedCostComponent(uiSelectedPassThrough));
    // Update ref to preserve selections

    selectedCostComponentsRef.current = uiSelectedPassThrough;
    const selectedCustomerClass =
      customerType?.value && customerType.value !== "All"
        ? customerType.value
        : null;
    dispatch(setCustomerClass(selectedCustomerClass));
    dispatch(setTaxIncluded(toNullableBoolean(taxable)));
    dispatch(setPor(toNullableBoolean(por)));

    const convertDateObjectToIso = (dObj: any | null | undefined) => {
      if (!dObj) return null;
      try {
        // react-multi-date-picker DateObject has toDate() and year/month/day
        if (typeof dObj.toDate === "function") {
          const date = dObj.toDate();
          return toIsoStringFromDate(date);
        }
        if (dObj instanceof Date) {
          return toIsoStringFromDate(dObj);
        }
        // Fallback if we get a string
        return toIsoDate(dObj) || null;
      } catch {
        return null;
      }
    };

    // Handle Effective Date (single date)
    if (Array.isArray(effectiveDate) && effectiveDate.length >= 2) {
      dispatch(setEffectiveFromDate(convertDateObjectToIso(effectiveDate[0])));
      dispatch(setEffectiveToDate(convertDateObjectToIso(effectiveDate[1])));
    } else if (effectiveDate) {
      const singleIso = convertDateObjectToIso(effectiveDate);
      dispatch(setEffectiveFromDate(null));
      dispatch(setEffectiveToDate(singleIso));
    } else {
      dispatch(setEffectiveFromDate(null));
      dispatch(setEffectiveToDate(null));
    }

    // Handle Start Date
    const startToDate = getValues("startToDate");
    if (startToDate) {
      const startIso = convertMonthSelectionToIso(startToDate);
      dispatch(setStartFromDate(null));
      dispatch(setStartToDate(startIso));
    } else {
      // If Start date is cleared, use default value (next month's date)
      const defaultStartIso = toIsoStringFromDate(getNextMonthStartDate());
      dispatch(setStartFromDate(null));
      dispatch(setStartToDate(defaultStartIso));
    }

    // Handle usage and demand
    const toBoundedInt = (
      val: any,
      min: number,
      max: number
    ): number | null => {
      if (val === null || val === undefined || val === "") return null;
      const num = Number(val);
      if (isNaN(num)) return null;
      return Math.max(min, Math.min(max, num));
    };

    dispatch(setUsage(toBoundedInt(usage, 0, 2000000)));
    dispatch(setDemand(toBoundedInt(demand, 0, 100000)));
    dispatch(setLoadFactor(toBoundedInt(loadFactor, 0, 100)));
    dispatch(fetchChartsData(clientId, accessToken, isPremium));
    // await extra?.onApply(data);
  };

  // --- Saved Filters Handlers ---
  const fetchSavedFilters = useCallback(async () => {
    try {
      const response = await loadUserFiltersApi({ pageKey: "Matrix", userToken: accessToken });
      if (response?.success && Array.isArray(response.data)) {
        const parsed = response.data.map((item: any) => ({
          id: item.rowKey,
          name: item.name,
          filters: typeof item.filters === "string" ? JSON.parse(item.filters) : item.filters,
        }));
        setSavedFiltersList(parsed);
        return parsed;
      }
    } catch (error) {
      console.error("Error loading saved filters:", error);
    }
    return [];
  }, [accessToken]);

  const handleSaveFilters = useCallback(async (filterName: string) => {
    setSavingFilters(true);
    try {
      const formValues = getValues();
      const serialized: any = {
        commodity: formValues.commodity,
        stateCode: formValues.stateCode,
        effectiveDate: formValues.effectiveDate instanceof Date
          ? dateToString(formValues.effectiveDate)
          : formValues.effectiveDate || null,
        startToDate: formValues.startToDate instanceof Date
          ? dateToString(formValues.startToDate)
          : formValues.startToDate || null,
        term: formValues.term,
        utility: formValues.utility,
        rateClass: formValues.rateClass,
        loadProfile: formValues.loadProfile,
        loadZone: formValues.loadZone,
        billingMethod: formValues.billingMethod,
        usage: formValues.usage,
        demand: formValues.demand,
        loadFactor: formValues.loadFactor,
        productType: formValues.productType,
        passThrough: formValues.passThrough,
        taxable: formValues.taxable,
        por: formValues.por,
        customerType: formValues.customerType,
      };
      const response = await saveUserFiltersApi({ pageKey: "Matrix", name: filterName, filters: serialized, userToken: accessToken });
      if (response?.success) {
        snackbar(response.message || "Filter saved successfully", "success");
        const updatedFilters = await fetchSavedFilters();
        if (response.rowKey) {
          setSelectedSavedFilterId(response.rowKey);
          setTimeout(() => {
            applySavedFilter(response.rowKey, updatedFilters);
          }, 100);
        }
      } else {
        snackbar(response?.message || "Failed to save filter", "error");
      }
    } catch (error: any) {
      console.error("Error saving filters:", error);
      snackbar(error?.message || "An error occurred while saving filter", "error");
    } finally {
      setSavingFilters(false);
    }
  }, [getValues, snackbar, fetchSavedFilters, accessToken]);

  const handleDeleteSavedFilter = useCallback(async (filterId: string) => {
    try {
      const response = await deleteUserFilterApi({ filterId, pageKey: "Matrix", userToken: accessToken });
      if (response?.success) {
        snackbar(response.message || "Filter deleted successfully", "success");
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        await fetchSavedFilters();
      } else {
        snackbar(response?.message || "Failed to delete filter", "error");
      }
    } catch (error: any) {
      console.error("Error deleting saved filter:", error);
      snackbar(error?.message || "An error occurred while deleting filter", "error");
    }
  }, [snackbar, fetchSavedFilters, selectedSavedFilterId, accessToken]);

  const applySavedFilter = useCallback((filterId: string, filterList?: any[]) => {
    const list = filterList || savedFiltersList;
    const savedFilter = list.find((f: any) => f.id === filterId);
    if (!savedFilter || !savedFilter.filters) return;
    setSelectedSavedFilterId(filterId);
    const f = savedFilter.filters;

    // Apply to form
    reset({
      commodity: f.commodity ?? "1",
      stateCode: f.stateCode ?? { id: "All", name: "All" },
      effectiveDate: f.effectiveDate ? new Date(f.effectiveDate) : getYesterdayDate(),
      startToDate: f.startToDate ? new Date(f.startToDate) : getNextMonthStartDate(),
      term: f.term ?? { id: "12", name: "12 Months", value: "12" },
      utility: f.utility ?? { id: "All", name: "All", value: "All" },
      rateClass: f.rateClass ?? { id: "All", value: "All", name: "All" },
      loadProfile: f.loadProfile ?? { id: "All", value: "All", name: "All" },
      loadZone: f.loadZone ?? { id: "All", value: "All", name: "All" },
      billingMethod: f.billingMethod ?? { id: "All", value: "All", name: "All" },
      usage: f.usage ?? null,
      demand: f.demand ?? null,
      loadFactor: f.loadFactor ?? null,
      productType: f.productType ?? getDefaultProductTypeSelection(),
      passThrough: f.passThrough ?? [{ id: "All", name: "All", value: "All" }],
      taxable: f.taxable ?? ALL_OPTION,
      por: f.por ?? ALL_OPTION,
      customerType: f.customerType ?? { id: "COMM", name: "Commercial", value: "COMM" },
    });

    // Apply to Redux
    dispatch(setCommodity(Number(f.commodity) || 1));
    if (f.stateCode && f.stateCode.id !== "All") {
      dispatch(setSelectedState({ value: f.stateCode.id, name: f.stateCode.name }));
    } else {
      dispatch(setSelectedState(null));
    }
    dispatch(setSelectedTerm(f.term?.value ?? "12"));
    if (f.utility && f.utility.id !== "All") {
      dispatch(setSelectedUtility({ value: f.utility.id || f.utility.value, name: f.utility.name }));
    } else {
      dispatch(setSelectedUtility(null));
    }
    if (f.rateClass && f.rateClass.id !== "All") {
      dispatch(setSelectedRateClass({ value: f.rateClass.id || f.rateClass.value, name: f.rateClass.name }));
    } else {
      dispatch(setSelectedRateClass(null));
    }
    if (f.loadProfile && f.loadProfile.id !== "All") {
      dispatch(setSelectedLoadProfile({ value: f.loadProfile.id || f.loadProfile.value, name: f.loadProfile.name }));
    } else {
      dispatch(setSelectedLoadProfile(null));
    }
    if (f.loadZone && f.loadZone.id !== "All") {
      dispatch(setSelectedLoadZone({ value: f.loadZone.id || f.loadZone.value, name: f.loadZone.name }));
    } else {
      dispatch(setSelectedLoadZone(null));
    }
    if (f.billingMethod && f.billingMethod.id !== "All") {
      dispatch(setSelectedBillingMethod({ value: f.billingMethod.id || f.billingMethod.value, name: f.billingMethod.name }));
    } else {
      dispatch(setSelectedBillingMethod(null));
    }
    dispatch(setUsage(f.usage ?? null));
    dispatch(setDemand(f.demand ?? null));
    dispatch(setLoadFactor(f.loadFactor ?? null));
    dispatch(setCustomerClass(f.customerType?.value ?? "COMM"));
    dispatch(setTaxIncluded(toNullableBoolean(f.taxable)));
    dispatch(setPor(toNullableBoolean(f.por)));
    if (f.productType) {
      dispatch(setSelectedProductType(f.productType));
    }
    if (f.passThrough) {
      dispatch(setSelectedCostComponent(f.passThrough.map((p: any) => p.value)));
    }
    if (f.startToDate) {
      dispatch(setStartToDate(toIsoStringFromDate(new Date(f.startToDate))));
    }
    if (f.effectiveDate) {
      const effDate = new Date(f.effectiveDate);
      dispatch(setEffectiveFromDate(toIsoStringFromDate(effDate)));
      dispatch(setEffectiveToDate(toIsoStringFromDate(effDate)));
    }

    setEffectiveResetKey((k) => k + 1);

    // Auto-submit the form after applying saved filter
    setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 100);
  }, [savedFiltersList, reset, dispatch, setValue]);

  // Load saved filters on mount and auto-apply the first one
  useEffect(() => {
    if (savedFiltersLoadedRef.current) return;
    savedFiltersLoadedRef.current = true;
    const loadAndApply = async () => {
      await fetchSavedFilters();
    };
    loadAndApply();
  }, [fetchSavedFilters]);

  const resetFilters = () => {
    // Unselect any saved filter
    setSelectedSavedFilterId(null);
    // Reset the auto-apply flag so we can reinitialize like page load
    hasAutoAppliedRef.current = false;
    hasInitializedFiltersRef.current = false;
    isInitialLoadRef.current = true;
    setIsInitialLoadComplete(false);

    // Clear all Redux state
    dispatch(setCommodity(1));
    dispatch(setSelectedMarket(null));
    dispatch(setSelectedState(null));
    dispatch(setSelectedTerm("12"));
    dispatch(setSelectedUtility(null));
    dispatch(setSelectedRateClass(null));
    dispatch(setSelectedLoadProfile(null));
    dispatch(setSelectedLoadZone(null));
    dispatch(setSelectedBillingMethod(null));
    dispatch(setEffectiveFromDate(null));
    dispatch(setEffectiveToDate(null));
    dispatch(setStartFromDate(null));
    const defaultStartIso = toIsoStringFromDate(getNextMonthStartDate());
    dispatch(setStartToDate(defaultStartIso));
    dispatch(setUsage(null));
    dispatch(setDemand(null));
    dispatch(setLoadFactor(null));
    dispatch(setCustomerClass("COMM"));
    // dispatch(setSelectedProductType([]));
    dispatch(setSelectedProductType([DEFAULT_PRODUCT_TYPE_VALUE]));
    // Reset Pass Through to default "All" in Redux and local ref
    dispatch(setSelectedCostComponent(["All"]));
    dispatch(setTaxIncluded(null));
    dispatch(setPor(null));
    selectedCostComponentsRef.current = ["All"];

    // Clear local options except states; we'll repopulate dependents below
    setAvailableUtilities([]);
    setAvailableRateClasses([]);
    // setCostComponentWarning(false);

    // Reset form to initial state
    reset({
      commodity: "1",
      stateCode: { id: "All", name: "All" },
      effectiveDate: getYesterdayDate(),
      term: { id: "12", name: "12 Months", value: "12" },
      utility: { id: "All", name: "All", value: "All" },
      rateClass: { id: "All", value: "All", name: "All" },
      loadProfile: { id: "All", value: "All", name: "All" },
      loadZone: { id: "All", value: "All", name: "All" },
      billingMethod: { id: "All", value: "All", name: "All" },
      usage: null,
      demand: null,
      loadFactor: null,
      productType: [],
      startToDate: getNextMonthStartDate(),
      // Reset Pass Through field to default "All"
      passThrough: [{ id: "All", name: "All", value: "All" }],
      taxable: ALL_OPTION,
      por: ALL_OPTION,
      customerType: { id: "COMM", name: "Commercial", value: "COMM" },
    });

    // Explicitly set term to "12" in the form after reset
    setTimeout(() => {
      if (setValue) {
        setValue("term", { id: "12", name: "12 Months", value: "12" });
      }
    }, 0);

    // Increment reset key to force date picker re-render
    setEffectiveResetKey((k) => k + 1);

    // Ensure market is set (ERCOT preferred) to rehydrate lists
    const defaultMarket =
      pricingState.markets?.find((m) => m.name.toLowerCase() === "ercot") ||
      pricingState.markets?.[0];
    if (defaultMarket) {
      dispatch(setSelectedMarket(defaultMarket as any));
    }

    // If current market has exactly one state, auto-select it and load dependents immediately
    const marketForReset = (defaultMarket ||
      pricingState.selectedMarket) as any;
    const commodityLabel =
      marketForReset?.commodity &&
      String(marketForReset.commodity).toLowerCase().includes("gas")
        ? "NatGas"
        : "Electric";
    setDropdownLoadingState("utilities", true);
    fetchUtilitiesApi({
      marketIdentifier:
        (defaultMarket as any)?.value ||
        (pricingState.selectedMarket as any)?.value,
      commodity: commodityLabel,
      userToken: accessToken,
    })
      .then((utilitiesList) => {
        setAvailableUtilities(utilitiesList);
      })
      .catch((err) => console.error("Error loading utilities", err))
      .finally(() => setDropdownLoadingState("utilities", false));
  };

  const onDeleteFilter = (filterName: string) => {
    switch (filterName) {
      case "Market": {
        dispatch(setSelectedMarket(null as any));
        break;
      }
      case "State": {
        dispatch(setSelectedState(null));
        dispatch(setSelectedUtility(null));
        dispatch(setSelectedRateClass(null));
        dispatch(setSelectedLoadProfile(null));
        dispatch(setSelectedLoadZone(null));
        setValue("stateCode", { id: "All", name: "All", value: "All" });
        setValue("utility", { id: "All", name: "All", value: "All" });
        setValue("rateClass", { id: "All", name: "All", value: "All" });
        setValue("loadProfile", { id: "All", name: "All", value: "All" });
        setValue("loadZone", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Utility": {
        dispatch(setSelectedUtility(null));
        dispatch(setSelectedRateClass(null));
        setValue("utility", { id: "All", name: "All", value: "All" });
        setValue("rateClass", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Load Zone": {
        dispatch(setSelectedLoadZone(null));
        setValue("loadZone", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Rate Class": {
        dispatch(setSelectedRateClass(null));
        setValue("rateClass", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Load Profile": {
        dispatch(setSelectedLoadProfile(null));
        setValue("loadProfile", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Billing Method": {
        dispatch(setSelectedBillingMethod(null));
        setValue("billingMethod", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Customer Class": {
        dispatch(setCustomerClass(null));
        setValue("customerType", { id: "All", name: "All", value: "All" });
        break;
      }
      case "Term": {
        dispatch(setSelectedTerm("12"));
        setValue("term", { id: "12", name: "12 Months", value: "12" });
        break;
      }
      case "Effective Date": {
        dispatch(setEffectiveFromDate(null));
        dispatch(setEffectiveToDate(toIsoStringFromDate(getYesterdayDate())));
        setValue("effectiveDate", getYesterdayDate());
        break;
      }
      case "Start Date": {
        dispatch(setStartFromDate(null));
        dispatch(setStartToDate(toIsoStringFromDate(getNextMonthStartDate())));
        setValue("startToDate", getNextMonthStartDate());
        break;
      }
      case "Usage": {
        dispatch(setUsage(null));
        setValue("usage", null);
        break;
      }
      case "Demand": {
        dispatch(setDemand(null));
        setValue("demand", null);
        break;
      }
      case "Load Factor":
      case "Load Factor (%)": {
        dispatch(setLoadFactor(null));
        setValue("loadFactor", null);
        break;
      }
      case "Product Type": {
        dispatch(setSelectedProductType([]));
        setValue("productType", []);
        break;
      }
      case "Pass Through": {
        // Reset Pass Through to default "All" when badge is cleared
        dispatch(setSelectedCostComponent(["All"]));
        selectedCostComponentsRef.current = ["All"];
        setValue("passThrough", [{ id: "All", name: "All", value: "All" }]);
        break;
      }
      default:
        return;
    }

    // re-fetch with updated filters
    if (accessToken && isPremium !== null) {
      if (isPremium === true) {
        dispatch(fetchChartsData(clientId, accessToken, isPremium));
      } else {
        applyNonPremiumStaticCharts();
      }
    }
  };
  return (
    <>
      <div className="mb-2" style={{ marginTop: '1px' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="my-2 py-0">Ranking</h4>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontWeight: 800, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>Saved Filters:</span>
              <Dropdown>
                <Dropdown.Toggle variant="link" className="p-0 shadow-none text-decoration-none" style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', lineHeight: 1, paddingBottom: '1px' }}>
                  {savedFiltersList && savedFiltersList.length > 0 && selectedSavedFilterId
                    ? (savedFiltersList.find((f: any) => f.id === selectedSavedFilterId)?.name || "Default")
                    : "Default"}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: '220px' }}>
                  <Dropdown.Item active={!selectedSavedFilterId} onClick={() => resetFilters()}>Default</Dropdown.Item>
                  {savedFiltersList && savedFiltersList.map((f: any) => (
                    <Dropdown.Item key={f.id} className="d-flex justify-content-between align-items-center" active={f.id === selectedSavedFilterId} onClick={() => applySavedFilter(f.id)}>
                      <span>{f.name}</span>
                      <span className="text-muted" style={{ cursor: "pointer", fontSize: "10px", lineHeight: 1, marginLeft: '8px' }} onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleDeleteSavedFilter(f.id); }} title="Delete filter">
                        <CloseIcon />
                      </span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button size="sm" type="button" variant="primary" onClick={resetFilters} disabled={isPremium === false}>Reset</Button>
              <Button size="sm" type="button" variant="primary" onClick={() => { setShowSaveModal(true); setSaveFilterName(""); }} disabled={isPremium === false || savingFilters}>Save Filters</Button>
              <Button size="sm" type="button" variant="primary" onClick={() => handleSubmit(onSubmit)()} disabled={isPremium === false}>Apply</Button>
            </div>
          </div>
        </div>
      </div>
      {/* Save Filter Name Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        centered
        size="sm"
        title="Save Filter"
        confirmButtonLabel={savingFilters ? "Saving..." : "Save & Apply"}
        isLoading={savingFilters}
        onConfirm={() => {
          if (saveFilterName.trim()) {
            handleSaveFilters(saveFilterName.trim());
            setShowSaveModal(false);
            setSaveFilterName("");
          }
        }}
      >
          <FormInput type="text" name="saveFilterName" placeholder="Enter filter name" value={saveFilterName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSaveFilterName(e.target.value)} autoFocus />
      </ConfirmationModal>
      <Card className="ranking-module">
        <Card.Body>
          <Row>
            <Col md={12}>
              {/* <StickyDiv>
              <SelectedFilters onDeleteFilter={onDeleteFilter}/>
            </StickyDiv> */}
              <AccordionFilter
                hide={() => {}}
                control={control}
                register={register}
                handleSubmit={handleSubmit(onSubmit)}
                errors={errors}
                reset={resetFilters}
                onDeleteFilter={onDeleteFilter}
                resetKey={effectiveResetKey}
                trigger={trigger}
                adminClients={availableAdminClients}
                dependantDropdowns={{
                  utilities: availableUtilities,
                  rateClasses: availableRateClasses,
                  loadProfiles: availableLoadProfiles,
                  loadZones: availableLoadZones,
                  billingMethods: availableBillingMethods,
                  customerTypes: availableCustomerTypes,
                  states: availableStates,
                }}
                dropdownLoading={dropdownLoading}
                setValue={setValue}
                filterActionsDisabled={isPremium === false}
                savedFilters={savedFiltersList}
                onSaveFilters={handleSaveFilters}
                onDeleteSavedFilter={handleDeleteSavedFilter}
                onApplySavedFilter={applySavedFilter}
                savingFilters={savingFilters}
                selectedSavedFilterId={selectedSavedFilterId}
              />
            </Col>
            {/* <Col md={2} className="d-flex justify-content-end align-items-center mb-3">
              <StickyDiv>
                <Button
                variant="primary"
                className="mx-1"
                onClick={openFilterDrawer}
              >
                <FiltersIcon />
              </Button>
              </StickyDiv>
            </Col> */}
            <Col md={12}>
              <ChartSection></ChartSection>
              {isFilterDrawerOpen && (
                <Drawer
                  isOpen={isFilterDrawerOpen}
                  hide={closeFilterDrawer}
                  content={
                    <PricingMatrixFilters
                      hide={closeFilterDrawer}
                      control={control}
                      register={register}
                      handleSubmit={handleSubmit(onSubmit)}
                      errors={errors}
                      reset={resetFilters}
                      dependantDropdowns={{
                        utilities: availableUtilities,
                        rateClasses: availableRateClasses,
                        loadZones: availableLoadZones,
                        billingMethods: availableBillingMethods,
                        customerTypes: availableCustomerTypes,
                        states: availableStates,
                      }}
                      dropdownLoading={dropdownLoading}
                      // filters={filters}
                      // setFilters={setFilters}
                      // extra={extra}
                    />
                  }
                />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default Pricing;
